import API from 'api';

import { withLoading } from 'utils';
import {
  SET_TTS_VOICE,
  SET_TTS_LOCALE,
  SET_ELEVENLABS_TTS_LOCALE,
  SET_ELEVENLABS_TTS_ACCENT,
  SET_ELEVENLABS_TTS_VOICE,
  SET_ELEVENLABS_TTS_GENDER,
} from 'actions/types';
import { showError } from 'actions/statusActions';

const filter = 'flinkMake/tts';

const HAITIAN_TTS_MAX_POLL_MS = 30 * 60 * 1000;
const HAITIAN_TTS_STEADY_POLL_INTERVAL_MS = 1000;
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getHaitianTtsPollDelayMs = (attempt) => {
  if (attempt === 0) return 750;
  return HAITIAN_TTS_STEADY_POLL_INTERVAL_MS;
};

const waitForHaitianTtsJob = async ({ jobId, path, locale, useFileBucket }) => {
  const startedAt = Date.now();
  let attempt = 0;

  while (Date.now() - startedAt < HAITIAN_TTS_MAX_POLL_MS) {
    await delay(getHaitianTtsPollDelayMs(attempt));
    attempt += 1;

    const result = await API.polly.getHaitianTtsJobStatus({
      jobId,
      path,
      locale,
      useFileBucket,
    });

    if (!result || result.success === false) {
      if (result?.retryable === true && result?.errorCode === 'HAITIAN_TTS_LOCAL_STATUS_FAILED') continue;
      return result || { success: false, error: 'Haitian TTS job status failed' };
    }
    if (result.filename) return result;
    if (!result.pending) {
      return {
        success: false,
        error: `Haitian TTS job ended without an audio file (status=${result.status || 'unknown'})`,
      };
    }
  }

  return {
    success: false,
    error: 'Haitian TTS job did not finish within 30 minutes',
  };
};

export const generateTTS = (params) => (dispatch) => {
  const {
    voice,
    locale,
    text,
    path,
    useFileBucket,
    fileToDelete,
    provider,
    elevenLabsVoiceId,
    haitianVoiceId,
    haitianAccent,
  } = params;

  return withLoading(dispatch, async () => {
    let result = await API.polly.generateTTS({
      path,
      voice,
      locale,
      text,
      useFileBucket,
      provider,
      elevenLabsVoiceId,
      haitianVoiceId,
      haitianAccent,
    });

    if (provider === 'haitian-creole' && result?.pending && result?.jobId) {
      result = await waitForHaitianTtsJob({
        jobId: result.jobId,
        path,
        locale,
        useFileBucket,
      });
    }

    const { filename, error } = result || {};
    if (!filename) {
      dispatch(showError({
        message: error || 'TTS generation failed',
      }));
      return false;
    }

    if (fileToDelete) {
      const key = `${path}/${fileToDelete}`;
      await API.storage.deleteFiles([key]);
    }

    return filename;
  });
}

export const setTTSVoice = (voice) => ({
  filter,
  type: SET_TTS_VOICE,
  payload: voice,
});

export const setTTSLocale = (locale) => ({
  filter,
  type: SET_TTS_LOCALE,
  payload: locale,
});

export const setElevenLabsTTSLocale = (locale) => ({
  filter,
  type: SET_ELEVENLABS_TTS_LOCALE,
  payload: locale,
});

export const setElevenLabsTTSAccent = (accent) => ({
  filter,
  type: SET_ELEVENLABS_TTS_ACCENT,
  payload: accent,
});

export const setElevenLabsTTSVoice = (gender, voiceId) => ({
  filter,
  type: SET_ELEVENLABS_TTS_VOICE,
  payload: { gender, voiceId },
});

export const setElevenLabsTTSGender = (gender) => ({
  filter,
  type: SET_ELEVENLABS_TTS_GENDER,
  payload: gender,
});
